import React, { Component } from 'react';
import { observer, inject } from 'mobx-react';
import { MdCloudCircle } from 'react-icons/md';

import { Text, Heading, ProgressBar, Box, Icon } from 'core/components';
import { ReactComponent as FlowPakIcon } from 'app/assets/icons/flowpak-icon.svg';
import { addCommas } from 'app/util/utils';

import UniversalCloudPakCard from 'app/views/licenses/UniversalCloudPakCard';
import FpsAlert from 'app/components/cloudpakFlowIndicators/FpsAlert';
import CloudProviderStrip from './CloudProviderStrip';
import LabelValue from './LabelValue';
import PlanDevicesPopover from './PlanDevicesPopover';
import { PlanCardContainer, PlanCardLeftFlex, PlanCardMainContentWrapper, PlanIconBox } from './layout';
import FreeTrialCallout from './FreeTrialCallout';

@inject('$devices', '$auth')
@observer
class PlanCard extends Component {
  state = {
    alertData: null
  };

  handleAlertDataChange = (alertData) => {
    this.setState({ alertData });
  };

  renderCloudCard = () => {
    const { $auth, $devices, id, pak_fps } = this.props;
    if ($auth.isSudoer) {
      return <UniversalCloudPakCard maxFps={pak_fps} onAlertDataChange={this.handleAlertDataChange} {...this.props} />;
    }
    return (
      <CloudProviderStrip
        numAzureDevices={$devices.getCloudDeviceCount('azure', id)}
        numGcpDevices={$devices.getCloudDeviceCount('gce', id)}
        numAwsDevices={$devices.getCloudDeviceCount('aws', id)}
      />
    );
  };

  render() {
    const {
      id,
      name: planName,
      description: planDescription,
      bgp,
      device_count,
      max_devices,
      max_fps,
      free_credits,
      paid_credits,
      pak_fps,
      cloud_provider,
      fast_retention,
      full_retention,
      plan_type,
      trialDaysRemaining,
      usedSyntheticCredits,
      $devices,
      $auth,
      openContactAccountTeamDialog,
      ...rest
    } = this.props;

    let name = planName;
    let bgpText = `${bgp ? 'Enabled (Full)' : 'Disabled'}`;
    let pak;
    let icon;

    const showPlanDescription = !cloud_provider;
    const isFreeTrialPlan = planName === 'Free Trial Plan';
    const isUniversalCloudPlan = plan_type === 'cloud' && cloud_provider === 'universal';
    const isCorePlan = plan_type === 'core';
    const isEdgePlan = plan_type === 'edge';
    const isCloudPlan = plan_type === 'cloud' && cloud_provider !== 'universal';
    const isFlowPak = plan_type === 'flowpak';
    const isUniversalPlan = plan_type === 'universalpak';

    const showAllDevicesPopoverLink = !isUniversalCloudPlan;

    // "legacy" includes more than just plan_type legacy
    const isLegacyPlan = ['core', 'legacy', 'edge'].includes(plan_type);

    // Per-provider cloud plans are represented by the Universal CloudPak card instead
    if (isCloudPlan) {
      return null;
    }

    if (isUniversalCloudPlan) {
      icon = <Icon icon={MdCloudCircle} iconSize={40} color="primary" />;
      pak = 'CloudPak';
      name = 'Universal';
    }

    if (isFlowPak) {
      icon = <FlowPakIcon style={{ width: 40, height: 40 }} />;
      pak = 'FlowPak';
      bgpText = `${bgp ? 'Enabled (Partial)' : 'Disabled'}`;
    }

    if (plan_type === 'legacy') {
      icon = <Icon icon="cube" iconSize={40} color="warning" />;
    }

    if (isCorePlan) {
      icon = <Icon icon="cog" iconSize={40} color="warning" />;
      pak = 'Core';
      bgpText = `${bgp ? 'Enabled (Partial)' : 'Disabled'}`;
    }

    if (isEdgePlan) {
      icon = <Icon icon="cube" iconSize={40} color="primary" />;
      pak = 'Edge';
    }

    if (isUniversalPlan) {
      icon = <Icon icon="globe-network" iconSize={40} color="primary" />;
      pak = 'UniversalPak';
    }

    return (
      <PlanCardContainer {...rest}>
        <PlanCardLeftFlex>
          {icon && <PlanIconBox>{icon}</PlanIconBox>}

          <PlanCardMainContentWrapper>
            {pak && (
              <Text color="muted" as="div" mb="4px">
                {pak}
              </Text>
            )}

            <Heading level={4} mb={1} mt={1}>
              {name}{' '}
              <Text fontWeight={200} muted>
                ({id})
              </Text>
            </Heading>

            {(isUniversalCloudPlan || isUniversalPlan) && this.renderCloudCard()}

            {showPlanDescription && (
              <Text color="muted" as="div" mb={1}>
                {planDescription}
              </Text>
            )}

            {/* hide no devices count if Universal, because there's already a display that indicates this per cloud provider */}
            {device_count === 0 && !isUniversalCloudPlan && (
              <Text color="muted">No devices have been added to this plan.</Text>
            )}

            {isLegacyPlan && (
              <Box mt="4px">
                <Box mb={1}>
                  <ProgressBar animate={false} stripes={false} value={device_count / max_devices} intent="primary" />
                </Box>
                <Text fontWeight="bold">{device_count}</Text> of{' '}
                <Text color="muted" textAlign="right">
                  {max_devices} devices
                </Text>{' '}
                {device_count > 0 && <PlanDevicesPopover planId={id} showViewAllLink={showAllDevicesPopoverLink} />}
              </Box>
            )}

            {isFreeTrialPlan && <FreeTrialCallout trialDaysRemaining={trialDaysRemaining} />}
          </PlanCardMainContentWrapper>
        </PlanCardLeftFlex>
        <Box>
          <Text color="muted" as="div" fontWeight="normal" mb={1}>
            Entitlements
          </Text>

          {/* CloudPaks use a new `pak_fps` to indicate "sold" FPS */}
          <LabelValue
            label="Max FPS"
            value={
              isFlowPak || isUniversalCloudPlan || isUniversalPlan ? addCommas(parseInt(pak_fps)) : addCommas(max_fps)
            }
          />
          {!isLegacyPlan && (
            <LabelValue
              label={isUniversalCloudPlan ? 'Data Sources' : 'Devices'}
              value={
                <>
                  <Box as="span" pr="4px">
                    {device_count}
                    {/* Cloud (universal or paks) have no device limit */}
                    {!isUniversalCloudPlan && <> of {max_devices}</>}
                  </Box>
                  {device_count > 0 && <PlanDevicesPopover planId={id} showViewAllLink={showAllDevicesPopoverLink} />}
                </>
              }
            />
          )}
          {!isUniversalCloudPlan && <LabelValue label="BGP" value={bgpText} />}
          <LabelValue label="Fast Retention" value={`${fast_retention} days`} />
          <LabelValue label="Full Retention" value={`${full_retention} days`} mb={0} />

          {/* FPS Alerts */}
          {(() => {
            const { alertData } = this.state;
            if (!alertData) {
              return null;
            }

            const rawReceivedFps = alertData?.receivedFps;
            const rawMaxFps = alertData?.maxFps;
            const receivedFps = rawReceivedFps != null ? Number(rawReceivedFps) : NaN;
            const maxFps = rawMaxFps != null ? Number(rawMaxFps) : NaN;
            const receivedExceedsMaxFps =
              Number.isFinite(receivedFps) && Number.isFinite(maxFps) && receivedFps > maxFps;

            return (
              <Box mt={2}>
                <FpsAlert
                  type="raw"
                  avgConfiguredSamplingRate={alertData.avgConfiguredSamplingRate}
                  openContactAccountTeamDialog={openContactAccountTeamDialog}
                />
                <FpsAlert
                  type="received"
                  dataResolution={alertData.dataResolution}
                  receivedExceedsMaxFps={receivedExceedsMaxFps}
                  openContactAccountTeamDialog={openContactAccountTeamDialog}
                />
              </Box>
            );
          })()}
        </Box>
      </PlanCardContainer>
    );
  }
}

export default PlanCard;
